// React glue for voice input. Wires the pure strategy ladder (voice.ts) to the
// live browser APIs and the /transcribe fallback. Fills the composer via
// `onText`; never sends — the user reviews and hits send.

import { useCallback, useEffect, useRef, useState } from 'react';
import { transcribeAudio, TranscribeUnavailableError } from './api.ts';
import {
  detectVoiceCaps,
  effectiveStrategy,
  getSpeechRecognitionCtor,
  transcriptFromEvent,
  type VoiceStrategy,
  type SpeechRecognitionLike,
} from './voice.ts';

export interface UseVoiceInput {
  /** 'speech' | 'record' | 'unavailable' — 'unavailable' hides the mic. */
  strategy: VoiceStrategy;
  listening: boolean;
  /** Non-fatal status (e.g. mic permission denied) shown as a tooltip. */
  error: string | null;
  toggle: () => void;
}

/**
 * @param onText   called with the (partial or final) transcript to place in the
 *                 composer. Speech mode calls it live; record mode once on stop.
 */
export function useVoiceInput(onText: (text: string) => void): UseVoiceInput {
  const [transcribeDisabled, setTranscribeDisabled] = useState(false);
  const [listening, setListening] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const recognizerRef = useRef<SpeechRecognitionLike | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const onTextRef = useRef(onText);
  onTextRef.current = onText;

  const caps = detectVoiceCaps(typeof window !== 'undefined' ? window : undefined);
  const strategy = effectiveStrategy(caps, transcribeDisabled);

  const stopSpeech = useCallback(() => {
    recognizerRef.current?.stop();
  }, []);

  const startSpeech = useCallback(() => {
    const Ctor = getSpeechRecognitionCtor(window);
    if (!Ctor) return;
    const rec = new Ctor();
    rec.lang = navigator.language || 'en-US';
    rec.continuous = true;
    rec.interimResults = true;
    rec.onresult = (event) => onTextRef.current(transcriptFromEvent(event));
    rec.onerror = (e) => {
      setError(e.error === 'not-allowed' ? 'Microphone access blocked' : 'Voice input failed');
      setListening(false);
    };
    rec.onend = () => {
      setListening(false);
      recognizerRef.current = null;
    };
    recognizerRef.current = rec;
    setError(null);
    setListening(true);
    rec.start();
  }, []);

  const stopRecording = useCallback(() => {
    recorderRef.current?.stop(); // fires onstop → transcribe
  }, []);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const recorder = new MediaRecorder(stream);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        setListening(false);
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' });
        chunksRef.current = [];
        if (blob.size === 0) return;
        try {
          const { text } = await transcribeAudio(blob);
          if (text) onTextRef.current(text);
        } catch (err) {
          if (err instanceof TranscribeUnavailableError) {
            setTranscribeDisabled(true); // latch: hides the mic from now on
          } else {
            setError('Transcription failed');
          }
        }
      };
      recorderRef.current = recorder;
      setError(null);
      setListening(true);
      recorder.start();
    } catch {
      setError('Microphone access blocked');
      setListening(false);
    }
  }, []);

  const toggle = useCallback(() => {
    if (strategy === 'unavailable') return;
    if (listening) {
      if (strategy === 'speech') stopSpeech();
      else stopRecording();
      return;
    }
    if (strategy === 'speech') startSpeech();
    else void startRecording();
  }, [strategy, listening, startSpeech, stopSpeech, startRecording, stopRecording]);

  // Tear down on unmount so a live mic never leaks past the panel closing.
  useEffect(() => {
    return () => {
      recognizerRef.current?.abort?.();
      recorderRef.current?.stop();
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  return { strategy, listening, error, toggle };
}
